// @flow

import React from 'react';
import styled, { css } from 'styled-components';
import media from '../../../helpers/media';
import { ResponsiveTableRow, Table, Cell } from '../../table';
import { Price, CompareItem, AddButton as AddButtonComponent } from '../atoms';
import rows from './rows';
import ComparedPrice from '../../price';
import TitleComponent from '../../title';
import { type PdbPrices } from '../../../store/pdbComparison';
import type { Theme } from '../../../types';

type Props = {
  pdbIds: Array<string>,
  prices: PdbPrices,
  comparedPrices: Array<number>,
  compareHeadingText: string,
  buttonOrderText: string,
  buttonModelInfoText: string,
  rowHeadingModelText: string,
  rowHeadingTypeText: string,
  rowHeadingColorText: string,
  rowHeadingDeliveryTimeText: string,
  rowHeadingDurationText: string,
  rowHeadingKmpackText: string,
  rowHeadingClaimFreeText: string,
  rowHeadingPriceDifferenceText: string,
  rowHeadingPricePerKmText: string,
  rowHeadingPriceText: string,
};

const Title = styled<typeof TitleComponent, Theme>(TitleComponent)`
  margin-bottom: 1.75rem;
`;

const AddButton = styled<typeof AddButtonComponent, Theme>(AddButtonComponent)`
  ${media.lg(css`
    margin-top: 4.5rem;
  `)}
`;

const getComparedPriceOrEmpty = (comparedPrice, index) => (index === 0 ? (
  <>-</>
) : (
  <ComparedPrice displayCurrencySymbol amount={comparedPrice} />
));

const TOTAL_COLUMNS = 3;
const FIXED_ROWS = 3;
const COLUMN_WIDTH_PERCENTAGE = 25;

const CompareTable = ({
  pdbIds,
  prices,
  comparedPrices,
  compareHeadingText,
  buttonOrderText,
  buttonModelInfoText,
  rowHeadingModelText,
  rowHeadingTypeText,
  rowHeadingColorText,
  rowHeadingDeliveryTimeText,
  rowHeadingDurationText,
  rowHeadingKmpackText,
  rowHeadingClaimFreeText,
  rowHeadingPriceDifferenceText,
  rowHeadingPricePerKmText,
  rowHeadingPriceText,
}: Props) => {
  const totalRows = rows.length + FIXED_ROWS;

  // check if all the spots are filled
  const isFull = pdbIds.length === TOTAL_COLUMNS;

  // how much colspan is there needed for the responsive heading
  const responsiveColspan = TOTAL_COLUMNS;

  // this is the width of the last column of the table (the add button)
  const remainderColumWidth = pdbIds.length === 1
    ? COLUMN_WIDTH_PERCENTAGE * 2
    : COLUMN_WIDTH_PERCENTAGE;

  const rowTextMapping = {
    type: rowHeadingTypeText,
    color: rowHeadingColorText,
    deliveryTime: rowHeadingDeliveryTimeText,
    duration: rowHeadingDurationText,
    claimFree: rowHeadingClaimFreeText,
    kmPack: rowHeadingKmpackText,
    priceDifferencePerKm: rowHeadingPricePerKmText,
  };

  return (
    <>
      <Title>{compareHeadingText}</Title>
      <Table>
        <tbody>
          <ResponsiveTableRow
            label={rowHeadingModelText}
            responsiveColspan={responsiveColspan}
          >
            <>
              {pdbIds.map((pdbId, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Cell cellWidth={COLUMN_WIDTH_PERCENTAGE} key={index}>
                  <CompareItem pdbId={pdbId} />
                </Cell>
              ))}
              {!isFull && (
                <Cell
                  noRightBorder
                  cellWidth={remainderColumWidth}
                  rowSpan={totalRows}
                >
                  <AddButton />
                </Cell>
              )}
            </>
          </ResponsiveTableRow>
          {rows.map(({ textKey, Component }) => {
            const rowHeadingText = rowTextMapping[textKey];
            return (
              <ResponsiveTableRow
                label={rowHeadingText}
                responsiveColspan={responsiveColspan}
                key={rowHeadingText}
              >
                {pdbIds.map(pdbId => (
                  <Cell cellWidth={COLUMN_WIDTH_PERCENTAGE} key={pdbId}>
                    <Component variant="small" blocksPerRow={1} pdbId={pdbId} />
                  </Cell>
                ))}
              </ResponsiveTableRow>
            );
          })}
          <ResponsiveTableRow
            label={rowHeadingPriceDifferenceText}
            responsiveColspan={responsiveColspan}
          >
            {comparedPrices.map((comparedPrice, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={index}>
                {getComparedPriceOrEmpty(comparedPrice, index)}
              </Cell>
            ))}
          </ResponsiveTableRow>
          <ResponsiveTableRow
            label={rowHeadingPriceText}
            responsiveColspan={responsiveColspan}
          >
            {prices.map(({ pdbId, price }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={index}>
                <Price
                  price={price}
                  pdbId={pdbId}
                  orderLabel={buttonOrderText}
                  moreInfoLabel={buttonModelInfoText}
                />
              </Cell>
            ))}
          </ResponsiveTableRow>
        </tbody>
      </Table>
    </>
  );
};

export default CompareTable;
